import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { LayoutGrid, Mic, Upload, ClipboardPaste, Star, AudioLines, FileCheck2, Brain, Crown, LogOut, Menu, X, ShieldCheck, Gift } from "lucide-react";
import { useEffect } from "react";
import { Logo, LogoMark } from "@/components/Logo";
import { ThemeToggle, useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { subjectsOf, subjectMeta, usageText } from "@/lib/constants";
import { SubjectManager } from "@/components/app/SubjectManager";
import { ConsentGate } from "@/components/app/ConsentGate";

const LIBRARY = [
  ["Mes cours", "/app", LayoutGrid, null],
  ["Mes enregistrements", "/app?vue=enregistrements", AudioLines, "enregistrements"],
  ["Mes fiches de révision", "/app?vue=fiches", FileCheck2, "fiches"],
  ["Mes quiz", "/app?vue=quiz", Brain, "quiz"],
  ["Mes cours favoris", "/app?vue=favoris", Star, "favoris"],
];
const CREATE = [["Enregistrer un cours", "/app/enregistrer", Mic], ["Importer un audio", "/app/importer", Upload], ["Mode texte", "/app/texte", ClipboardPaste]];

const Item = ({ to, icon: Icon, label, active, onClick, testid }) => (
  <Link to={to} onClick={onClick} data-testid={testid}
    className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm transition-colors ${active ? "bg-accent font-semibold text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
    <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
  </Link>
);

const SidebarContent = ({ onNavigate }) => {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const vue = params.get("vue"), matiere = params.get("matiere");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const usage = user?.usage || { used_minutes: 0, limit_minutes: 60 };
  const pct = usage.limit_minutes == null ? 100 : Math.min(100, (usage.used_minutes / usage.limit_minutes) * 100);
  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-4">
      <div className="flex items-center justify-between px-1"><Logo to="/app" /><ThemeToggle /></div>
      <Link to="/app/enregistrer" onClick={onNavigate} data-testid="sidebar-record-cta"
        className="bg-brand flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_26px_-12px_hsl(var(--primary)/0.8)] transition-transform hover:-translate-y-0.5">
        <Mic className="h-4 w-4" /> Nouvel enregistrement
      </Link>
      <nav className="space-y-0.5">
        <p className="eyebrow mb-2 px-3">Bibliothèque</p>
        {LIBRARY.map(([l, to, Icon, v]) => (
          <Item key={l} to={to} icon={Icon} label={l} onClick={onNavigate} testid={`sidebar-link-${v || "cours"}`}
            active={pathname === "/app" && vue === v && !matiere} />
        ))}
      </nav>
      <nav className="space-y-0.5">
        <div className="mb-2 flex items-center justify-between px-3"><p className="eyebrow">Mes matières</p><SubjectManager /></div>
        {subjectsOf(user).map((s) => (
          <Item key={s} to={`/app?matiere=${encodeURIComponent(s)}`} icon={subjectMeta(s, user?.subject_styles).icon} label={s} onClick={onNavigate}
            testid={`sidebar-subject-${s}`} active={matiere === s} />
        ))}
      </nav>
      <nav className="space-y-0.5">
        <p className="eyebrow mb-2 px-3">Créer</p>
        {CREATE.map(([l, to, Icon]) => (
          <Item key={l} to={to} icon={Icon} label={l} onClick={onNavigate} testid={`sidebar-create-${to.split("/").pop()}`} active={pathname === to} />
        ))}
        {user?.is_admin && <Item to="/app/admin" icon={ShieldCheck} label="Administration" onClick={onNavigate} testid="sidebar-admin-link" active={pathname === "/app/admin"} />}
        <Item to="/app/parrainage" icon={Gift} label="Parrainage" onClick={onNavigate} testid="sidebar-referral-link" active={pathname === "/app/parrainage"} />
      </nav>
      <div className="mt-auto space-y-3">
        <div className="card-flat p-4" data-testid="sidebar-usage">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold">Offre {user?.plan_name}</span>
            <span className="text-muted-foreground">{usageText(user)}</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"><div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} /></div>
          {user?.plan !== "pro" && (
            <Link to="/app/offres" onClick={onNavigate} data-testid="sidebar-upgrade-link" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
              <Crown className="h-3.5 w-3.5" /> Passer à l'offre supérieure
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <NavLink to="/app/profil" onClick={onNavigate} data-testid="sidebar-profile-link" className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2 py-1.5 hover:bg-muted">
            <LogoMark className="h-8 w-8 rounded-full" iconClass="h-4 w-4" testid="sidebar-profile-avatar" />
            <span className="min-w-0"><span className="block truncate text-sm font-semibold">{user?.name}</span><span className="block truncate text-xs text-muted-foreground">{user?.email}</span></span>
          </NavLink>
          <button data-testid="logout-button" aria-label="Se déconnecter" onClick={async () => { await logout(); navigate("/"); }}
            className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { setGradient } = useTheme();
  useEffect(() => { if (user?.theme_gradient) setGradient(user.theme_gradient); }, [user?.theme_gradient, setGradient]);
  if (!user?.consent_at) return <ConsentGate />;
  return (
    <div className="min-h-screen lg:pl-72" data-testid="app-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 border-r bg-card/50 backdrop-blur-xl lg:block"><SidebarContent /></aside>
      <header className="glass sticky top-0 z-30 flex items-center justify-between border-x-0 border-t-0 px-4 py-3 lg:hidden">
        <Logo to="/app" />
        <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="rounded-full border p-2"><Menu className="h-5 w-5" /></button>
      </header>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[86%] max-w-xs bg-card shadow-2xl">
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="absolute right-3 top-3 z-10 rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
            <SidebarContent onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8 lg:py-12"><Outlet /></main>
    </div>
  );
}
